import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { supabase } from '@/lib/supabase';

interface AdminUser {
  id: string;
  email: string;
  display_name: string | null;
  created_at: string;
  created_by: string | null;
}

function formatDate(dateStr: string) {
  const date = new Date(dateStr);
  return date.toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function AdminAccounts() {
  const { isAuthenticated, isChecking, logout, adminEmail } = useAdminAuth();
  const navigate = useNavigate();
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Batch create state
  const [emailInput, setEmailInput] = useState('');
  const [batchPassword, setBatchPassword] = useState('');
  const [batchSubmitting, setBatchSubmitting] = useState(false);
  const [batchResult, setBatchResult] = useState<{ email: string; status: string; message: string }[] | null>(null);
  const [batchError, setBatchError] = useState<string | null>(null);

  // Delete state
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    if (!isChecking && !isAuthenticated) {
      navigate('/admin', { replace: true });
      return;
    }
    if (!isAuthenticated) return;
    fetchAdmins();
  }, [isAuthenticated, isChecking, navigate]);

  const fetchAdmins = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: rpcError } = await supabase.functions.invoke('manage-admin-users', {
        body: { action: 'list' },
      });
      if (rpcError) throw rpcError;
      if (!data.success) throw new Error(data.error || '加载失败');
      setAdmins(data.data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载管理员列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleBatchCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setBatchError(null);
    setBatchResult(null);

    // Parse emails from textarea (split by newline, comma, semicolon, or space)
    const emails = emailInput
      .split(/[\n,;]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    if (emails.length === 0) {
      setBatchError('请输入至少一个邮箱地址');
      return;
    }

    if (!batchPassword || batchPassword.length < 8) {
      setBatchError('密码至少需要8个字符');
      return;
    }

    setBatchSubmitting(true);
    try {
      const { data, error: rpcError } = await supabase.functions.invoke('manage-admin-users', {
        body: {
          action: 'batch_create',
          emails,
          password: batchPassword,
        },
      });
      if (rpcError) throw rpcError;
      if (!data.success) throw new Error(data.error || '创建失败');
      setBatchResult(data.results);
      // Refresh list
      await fetchAdmins();
      // Clear form if all succeeded
      const allOk = data.results.every((r: { status: string }) => r.status === 'created' || r.status === 'exists');
      if (allOk) {
        setEmailInput('');
        setBatchPassword('');
      }
    } catch (err) {
      setBatchError(err instanceof Error ? err.message : '批量创建失败');
    } finally {
      setBatchSubmitting(false);
    }
  };

  const handleDelete = async (email: string) => {
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      const { data, error: rpcError } = await supabase.functions.invoke('manage-admin-users', {
        body: {
          action: 'delete',
          email_to_delete: email,
        },
      });
      if (rpcError) throw rpcError;
      if (!data.success) throw new Error(data.error || '删除失败');
      setAdmins((prev) => prev.filter((a) => a.email !== email));
      setDeleteConfirm(null);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : '删除失败');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) return null;

  return (
    <main className="min-h-screen bg-background-100">
      {/* Header */}
      <header className="bg-background-50 border-b border-background-200/70 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to="/admin/dashboard"
              className="text-foreground-400 hover:text-foreground-600 p-1 rounded-lg transition-colors cursor-pointer"
              title="返回文章管理"
            >
              <i className="ri-arrow-left-line" />
            </Link>
            <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center">
              <i className="ri-user-settings-line text-accent-600 text-sm" />
            </div>
            <h1 className="font-heading text-base font-bold text-foreground-900">帐号管理</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={logout}
              className="bg-background-100 hover:bg-background-200 text-foreground-600 px-3 py-2 rounded-lg text-sm whitespace-nowrap transition-colors cursor-pointer inline-flex items-center gap-1"
            >
              <i className="ri-logout-box-r-line text-sm" />
              退出
            </button>
            <Link
              to="/"
              className="text-foreground-400 hover:text-foreground-600 px-2 py-2 rounded-lg text-sm whitespace-nowrap transition-colors cursor-pointer"
              title="返回网站"
            >
              <i className="ri-home-line" />
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 py-6 space-y-6">
        {/* Batch Create Section */}
        <div className="bg-background-50 rounded-xl border border-background-200/70 p-6">
          <h2 className="font-heading text-sm font-bold text-foreground-900 mb-1 flex items-center gap-2">
            <i className="ri-user-add-line text-accent-600" />
            批量新增管理员
          </h2>
          <p className="text-xs text-foreground-500 mb-5">
            一次创建多个管理员帐号，统一设置初始密码。新增的管理员使用邮箱 + 密码登录后台。
          </p>

          <form onSubmit={handleBatchCreate} className="space-y-4">
            <div>
              <label htmlFor="batch-emails" className="block text-xs font-medium text-foreground-700 mb-1.5">
                管理员邮箱
                <span className="text-foreground-400 ml-1 font-normal">（每行一个，或用逗号、分号分隔）</span>
              </label>
              <textarea
                id="batch-emails"
                value={emailInput}
                onChange={(e) => { setEmailInput(e.target.value); setBatchError(null); setBatchResult(null); }}
                placeholder="editor@example.com&#10;writer@example.com&#10;manager@example.com"
                rows={5}
                className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all resize-y font-mono"
              />
            </div>

            <div>
              <label htmlFor="batch-password" className="block text-xs font-medium text-foreground-700 mb-1.5">
                初始密码
                <span className="text-foreground-400 ml-1 font-normal">（至少 8 个字符，所有新帐号统一使用此密码）</span>
              </label>
              <input
                id="batch-password"
                type="text"
                value={batchPassword}
                onChange={(e) => { setBatchPassword(e.target.value); setBatchError(null); setBatchResult(null); }}
                placeholder="请设置一个安全的初始密码"
                className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all"
              />
            </div>

            {batchError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700 flex items-center gap-2">
                <i className="ri-error-warning-line" />
                {batchError}
              </div>
            )}

            <button
              type="submit"
              disabled={batchSubmitting}
              className="bg-accent-500 hover:bg-accent-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors cursor-pointer inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              {batchSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  正在创建...
                </>
              ) : (
                <>
                  <i className="ri-user-add-line text-sm" />
                  批量创建
                </>
              )}
            </button>
          </form>

          {/* Batch Results */}
          {batchResult && batchResult.length > 0 && (
            <div className="mt-5 bg-background-100 rounded-lg border border-background-200/70 p-4">
              <h3 className="text-xs font-semibold text-foreground-700 mb-3">创建结果</h3>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {batchResult.map((r, i) => (
                  <div
                    key={i}
                    className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-md ${
                      r.status === 'created'
                        ? 'bg-green-50 text-green-700'
                        : r.status === 'exists'
                          ? 'bg-amber-50 text-amber-700'
                          : 'bg-red-50 text-red-700'
                    }`}
                  >
                    <i
                      className={
                        r.status === 'created'
                          ? 'ri-checkbox-circle-line'
                          : r.status === 'exists'
                            ? 'ri-information-line'
                            : 'ri-close-circle-line'
                      }
                    />
                    <span className="font-mono font-medium">{r.email}</span>
                    <span className="text-foreground-400">—</span>
                    <span>{r.message}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Admin List Section */}
        <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
          <div className="px-6 py-4 border-b border-background-200/70 flex items-center justify-between">
            <h2 className="font-heading text-sm font-bold text-foreground-900 flex items-center gap-2">
              <i className="ri-shield-user-line text-foreground-500" />
              现有管理员
              <span className="text-xs font-normal text-foreground-500 ml-2">
                （共 {admins.length} 人）
              </span>
            </h2>
            <button
              onClick={fetchAdmins}
              className="text-foreground-400 hover:text-foreground-600 p-1.5 rounded-lg transition-colors cursor-pointer"
              title="刷新列表"
            >
              <i className="ri-refresh-line text-sm" />
            </button>
          </div>

          {deleteError && (
            <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700 flex items-center gap-2">
              <i className="ri-error-warning-line" />
              {deleteError}
              <button
                onClick={() => setDeleteError(null)}
                className="ml-auto text-red-500 hover:text-red-700 cursor-pointer"
              >
                <i className="ri-close-line" />
              </button>
            </div>
          )}

          {loading ? (
            <div className="p-6 space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-14 bg-background-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <p className="text-sm text-red-600 mb-3">{error}</p>
              <button
                onClick={fetchAdmins}
                className="bg-background-100 hover:bg-background-200 text-foreground-600 px-4 py-2 rounded-lg text-sm cursor-pointer transition-colors"
              >
                <i className="ri-refresh-line text-sm mr-1" />
                重新加载
              </button>
            </div>
          ) : admins.length === 0 ? (
            <div className="p-10 text-center">
              <div className="w-14 h-14 rounded-full bg-background-200 flex items-center justify-center mx-auto mb-3">
                <i className="ri-user-search-line text-xl text-foreground-400" />
              </div>
              <p className="text-sm text-foreground-500">暂无管理员</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-200/70 bg-background-100/50">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                      邮箱
                    </th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider hidden sm:table-cell">
                      显示名称
                    </th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider hidden md:table-cell">
                      添加时间
                    </th>
                    <th className="text-center px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider w-24">
                      身份
                    </th>
                    <th className="text-right px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider w-24">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-200/50">
                  {admins.map((admin) => {
                    const isSelf = admin.email === adminEmail;
                    const isFirst = admin.email === 'alexus@dingtalk.com';
                    return (
                      <tr key={admin.id} className="hover:bg-background-100/50 transition-colors">
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            <span className="text-foreground-900 font-medium font-mono text-xs">
                              {admin.email}
                            </span>
                            {isSelf && (
                              <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-accent-100 text-accent-700 whitespace-nowrap">
                                我
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-5 py-3 text-foreground-600 hidden sm:table-cell">
                          {admin.display_name || '—'}
                        </td>
                        <td className="px-5 py-3 text-foreground-500 text-xs hidden md:table-cell">
                          {formatDate(admin.created_at)}
                        </td>
                        <td className="px-5 py-3 text-center">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${
                            isFirst
                              ? 'bg-primary-100 text-primary-700'
                              : 'bg-secondary-100 text-secondary-700'
                          }`}>
                            {isFirst ? '超级管理员' : '管理员'}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-right">
                          {isSelf ? (
                            <span className="text-xs text-foreground-400">当前帐号</span>
                          ) : deleteConfirm === admin.email ? (
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={() => handleDelete(admin.email)}
                                disabled={deleteLoading}
                                className="px-2 py-1 rounded text-xs font-medium bg-red-500 text-white hover:bg-red-600 cursor-pointer whitespace-nowrap disabled:opacity-50"
                              >
                                {deleteLoading ? '...' : '确认'}
                              </button>
                              <button
                                onClick={() => setDeleteConfirm(null)}
                                className="px-2 py-1 rounded text-xs font-medium bg-background-200 text-foreground-600 hover:bg-background-300 cursor-pointer whitespace-nowrap"
                              >
                                取消
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setDeleteConfirm(admin.email)}
                              className="p-2 rounded-lg text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                              title="移除管理员"
                            >
                              <i className="ri-delete-bin-line text-sm" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="flex flex-wrap gap-4">
          <div className="bg-background-50 rounded-xl border border-background-200/70 px-5 py-4">
            <p className="text-xs text-foreground-500 mb-1">管理员总数</p>
            <p className="font-heading text-2xl font-bold text-foreground-900">{admins.length}</p>
          </div>
          <div className="bg-background-50 rounded-xl border border-background-200/70 px-5 py-4">
            <p className="text-xs text-foreground-500 mb-1">超级管理员</p>
            <p className="font-heading text-2xl font-bold text-primary-600">1</p>
          </div>
        </div>
      </section>
    </main>
  );
}